import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { Logger } from "@/utils/logger";

export const GeoResponseSchema = z.object({
  country: z.string(),
  region: z.string().nullable(),
});

export type GeoResponse = z.infer<typeof GeoResponseSchema>;

export interface GeoQueryArgs {
  ssrCountry: string;
  ssrRegion: string | undefined;
}

export function geoQueryKey() {
  return ["geo"] as const;
}

// Revalidate periodically so long-running sessions catch IP changes (travel,
// VPN flip, network switch) without requiring a reload. Tight cadence so a
// VPN flip on a passively-open tab (no focus event, no reconnect) gets
// reflected quickly. Cost per user is negligible — `/api/geo` is a tiny
// edge function reading already-injected headers.
const GEO_REFETCH_INTERVAL_MS = 30 * 1000;

async function fetchGeo(): Promise<GeoResponse> {
  const response = await fetch("/api/geo", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`geo revalidation failed: ${response.status}`);
  }
  return GeoResponseSchema.parse(await response.json());
}

export function geoQuery({ ssrCountry, ssrRegion }: GeoQueryArgs) {
  return {
    queryKey: geoQueryKey(),
    queryFn: fetchGeo,
    initialData: { country: ssrCountry, region: ssrRegion ?? null },
    // On a long-lived tab the SSR verdict can drift from the live IP (travel,
    // VPN flip). Mark initialData as updated at the epoch so the query is
    // considered stale on mount and refetches immediately to verify.
    initialDataUpdatedAt: 0,
    // Override the app-wide 30s staleTime (get-query-client.ts) so every
    // focus/reconnect event actually refetches. Without this, a VPN/network
    // flip immediately after a successful fetch would be ignored until the
    // 30s window elapses — the exact race we're trying to close.
    staleTime: 0,
    refetchInterval: GEO_REFETCH_INTERVAL_MS,
    retry: (failureCount: number, err: Error) => {
      const shouldRetry = failureCount < 3;
      if (!shouldRetry) Logger.error(err, { feature: "geo-revalidation" });
      return shouldRetry;
    },
  };
}

export function useGeoQuery(args: GeoQueryArgs) {
  return useQuery(geoQuery(args));
}
